import { useCallback, useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { authedGet } from "../api/customerAuthedFetch";
import InfoTip from "../components/InfoTip";
import PageTour from "../components/PageTour";
import { SLIDES_UPLOAD_ACTIVITY_TOUR_STEPS } from "../tour/portalTour";
import adminStyles from "../ui/AdminStyles";

const PAGE_TITLE = "Upload Activity";

const STATUS_OPTIONS = [
  "",
  "detected",
  "started",
  "uploading",
  "uploaded",
  "pipeline_started",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
  "skipped",
];

const STATUS_LABELS = {
  detected: "Detected",
  started: "Started",
  uploading: "Uploading",
  uploaded: "Uploaded",
  pipeline_started: "Pipeline started",
  processing: "Processing",
  succeeded: "Succeeded",
  failed: "Failed",
  cancelled: "Cancelled",
  skipped: "Skipped",
};

const CHANNEL_OPTIONS = ["", "agent", "portal"];

const REFRESH_OPTS = [
  { key: "off", label: "Manual", ms: 0 },
  { key: "5m", label: "Every 5 min", ms: 300_000 },
  { key: "10m", label: "Every 10 min", ms: 600_000 },
  { key: "30m", label: "Every 30 min", ms: 1_800_000 },
  { key: "1h", label: "Every 1 hour", ms: 3_600_000 },
];

const TIPS = {
  timezone:
    "Search window and displayed times use this zone. Records are stored in UTC.",
  from: "Start of the search window in the selected timezone (inclusive). Default is 7 days ago at 00:00.",
  to: "End of the search window in the selected timezone (inclusive). Default is today at 23:59.",
  status:
    "Latest lifecycle state of the upload session: Detected → Started → Uploading → Uploaded → Pipeline started → Processing → Succeeded / Failed / Cancelled / Skipped.",
  channel: "How the file was submitted: Smart agent or browser portal.",
  displayName: "Slide filename basename only (no local path).",
  hashPrefix: "First 12 hex characters of the file SHA-256. Use to find retries of the same file.",
  actor: "Cognito username of the user who submitted the upload (Smart Agent or portal).",
  errorContains: "Show sessions whose error message contains this text.",
  autoRefresh:
    "Reload the table on this interval using the current filters, or only when you click Search / Refresh.",
  search: "Apply the filters and reload now. Changing filters does not reload until you search or auto-refresh runs.",
  refresh: "Reload now using the current filters without changing them.",
  reset:
    "Restore the default search window (last 7 days, any status/channel, empty text filters) and search again.",
  colStarted:
    "When this upload attempt was first recorded, in the selected timezone. Click the row for the full timeline.",
  colExpand: "Click the row to expand or collapse session details and timeline.",
  colChannel: "How the file was submitted (for example agent or portal). Historical rows may show other channels.",
  colName: "Slide filename basename sent to the cloud (not the local path). For succeeded uploads, click the name to open slide details.",
  colApplication:
    "Catalog application selected for this upload (e.g. AIxURO). Shown when the portal or agent recorded it; older sessions may be blank.",
  colHash: "SHA-256 prefix used to correlate retries of the same file.",
  colStatus:
    "Current upload-session status.\n\nDetected: A new file was found locally and has not started uploading yet.\nStarted: An upload session was recorded and the file is about to be sent.\nUploading: The file is transferring to the cloud.\nUploaded: Transfer finished; waiting for processing to begin.\nPipeline started: Cloud processing has been started for this slide.\nProcessing: The slide is being checked and analyzed.\nSucceeded: Processing completed successfully.\nFailed: Upload or processing failed. See the Error column.\nCancelled: The upload was stopped by the user before completion.\nSkipped: This attempt was not processed (for example a duplicate of a file already handled).",
  colSlide:
    "Open slide details for successfully processed uploads. Shown only when status is Succeeded and a pipeline job ID is available.",
  colActor: "Cognito username of the user who submitted this upload.",
  colError: "Last error for this session, if any.",
};

const labelRowStyle = { display: "inline-flex", alignItems: "center", gap: 6 };
const fieldStyle = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12 };
const expandIconStyle = {
  display: "inline-block",
  width: 14,
  fontSize: 10,
  color: "#6b7280",
  textAlign: "center",
};

function LabelWithTip({ label, tip, children }) {
  return (
    <div style={fieldStyle}>
      <span style={labelRowStyle}>
        {label}
        <InfoTip text={tip} />
      </span>
      {children}
    </div>
  );
}

function ThWithTip({ children, tip, align, wide }) {
  return (
    <th style={adminStyles.th}>
      <span style={labelRowStyle}>
        {children}
        <InfoTip text={tip} align={align} wide={wide} />
      </span>
    </th>
  );
}

function daysAgoLocalISODate(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayLocalISODate() {
  return daysAgoLocalISODate(0);
}

function defaultTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function toUTCISOString(date, time, timeZone) {
  const local = new Date(`${date}T${time}:00`);
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(local)
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
  return new Date(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
  )
    .toISOString()
    .replace(/\.\d{3}Z$/, "Z");
}

function formatInTimezone(isoUtc, timeZone) {
  if (!isoUtc) return "—";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(isoUtc));
}

function formatStatusLabel(status) {
  const s = (status || "").toLowerCase();
  if (!s || s === "presigned") return "—";
  return STATUS_LABELS[s] || s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function visibleStatus(item) {
  const s = (item?.status || "").toLowerCase();
  if (s && s !== "presigned") return s;
  const timeline = item?.timeline || [];
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const ev = (timeline[i]?.event || "").toLowerCase();
    if (ev && ev !== "presigned") return ev;
  }
  return "started";
}

/** Succeeded uploads with a pipeline job can open Customer Slide Detail. */
function canOpenSlideDetail(item) {
  const status = visibleStatus(item);
  return status === "succeeded" && !!String(item?.job_id || "").trim();
}

function visibleTimeline(timeline) {
  return (timeline || []).filter((t) => (t.event || "").toLowerCase() !== "presigned");
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let bg = "#64748b";
  if (s === "succeeded") bg = "#16a34a";
  else if (s === "failed") bg = "#dc2626";
  else if (s === "cancelled") bg = "#94a3b8";
  else if (s === "skipped") bg = "#ca8a04";
  else if (s === "detected") bg = "#7c3aed";
  else if (s.includes("pipeline") || s === "processing") bg = "#2563eb";
  return (
    <span style={{ ...adminStyles.badge, background: bg }}>{formatStatusLabel(s)}</span>
  );
}

export default function CustomerUploadActivity() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  const ALL_TIMEZONES = Intl.supportedValuesOf("timeZone");
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [fromDate, setFromDate] = useState(() => daysAgoLocalISODate(7));
  const [fromTime, setFromTime] = useState("00:00");
  const [toDate, setToDate] = useState(() => todayLocalISODate());
  const [toTime, setToTime] = useState("23:59");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [hashPrefix, setHashPrefix] = useState("");
  const [actorId, setActorId] = useState("");
  const [errorQ, setErrorQ] = useState("");
  const [reloadSeq, setReloadSeq] = useState(0);
  const [refreshKey, setRefreshKey] = useState("off");

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const load = useCallback(
    async ({ append = false, token = null } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("limit", "50");
        if (fromDate) params.set("from", toUTCISOString(fromDate, fromTime, timezone));
        if (toDate) params.set("to", toUTCISOString(toDate, toTime, timezone));
        if (status) params.set("status", status);
        if (channel) params.set("channel", channel);
        if (displayName.trim()) params.set("display_name", displayName.trim());
        if (hashPrefix.trim()) params.set("content_hash_prefix", hashPrefix.trim());
        if (actorId.trim()) params.set("actor_id", actorId.trim());
        if (errorQ.trim()) params.set("error", errorQ.trim());
        if (token) params.set("next_token", token);

        const data = await authedGet(
          `/customer/slides/upload-activity?${params.toString()}`
        );
        const rows = data.items || [];
        setItems((prev) => (append ? [...prev, ...rows] : rows));
        setNextToken(data.next_token || null);
      } catch (e) {
        setError(e.message || "Failed to load upload activity");
        if (!append) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [fromDate, fromTime, toDate, toTime, timezone, status, channel, displayName, hashPrefix, actorId, errorQ]
  );

  useEffect(() => {
    load({ append: false });
  }, [reloadSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const opt = REFRESH_OPTS.find((o) => o.key === refreshKey);
    if (!opt || opt.ms === 0) return undefined;
    const id = setInterval(() => setReloadSeq((n) => n + 1), opt.ms);
    return () => clearInterval(id);
  }, [refreshKey]);

  const resetFilters = () => {
    setTimezone(defaultTimezone());
    setFromDate(daysAgoLocalISODate(7));
    setFromTime("00:00");
    setToDate(todayLocalISODate());
    setToTime("23:59");
    setStatus("");
    setChannel("");
    setDisplayName("");
    setHashPrefix("");
    setActorId("");
    setErrorQ("");
    setExpanded({});
    setReloadSeq((n) => n + 1);
  };

  const inputStyle = {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <div data-tour="slides-activity-intro">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0 }}>{PAGE_TITLE}</h1>
            <PageTour
              tourId="slides-upload-activity"
              steps={SLIDES_UPLOAD_ACTIVITY_TOUR_STEPS}
            />
          </div>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
            End-to-end status of each slide from first detection through processing.
            Sessions are kept for 30 days.
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            marginLeft: "auto",
          }}
        >
          <div
            data-tour="slides-activity-refresh"
            style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}
          >
            <div style={{ ...fieldStyle, alignItems: "flex-end" }}>
              <span style={labelRowStyle}>
                Auto-refresh
                <InfoTip text={TIPS.autoRefresh} align="end" />
              </span>
              <select
                value={refreshKey}
                onChange={(e) => setRefreshKey(e.target.value)}
                style={inputStyle}
              >
                {REFRESH_OPTS.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 1 }}>
              <button
                type="button"
                style={{
                  ...inputStyle,
                  height: 34,
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  background: "#fff",
                }}
                onClick={() => setReloadSeq((n) => n + 1)}
                disabled={loading}
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
              <InfoTip text={TIPS.refresh} align="end" />
            </div>
          </div>
          <div style={{ ...fieldStyle, alignItems: "flex-end" }} data-tour="slides-activity-timezone">
            <span style={labelRowStyle}>
              Timezone
              <InfoTip text={TIPS.timezone} align="end" />
            </span>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={inputStyle}>
              {ALL_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        data-tour="slides-activity-filters"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
          marginBottom: 16,
          padding: 14,
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          background: "#f8fafc",
        }}
      >
        <LabelWithTip label="From" tip={TIPS.from}>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
          <input type="time" value={fromTime} onChange={(e) => setFromTime(e.target.value)} style={inputStyle} />
        </LabelWithTip>
        <LabelWithTip label="To" tip={TIPS.to}>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
          <input type="time" value={toTime} onChange={(e) => setToTime(e.target.value)} style={inputStyle} />
        </LabelWithTip>
        <LabelWithTip label="Status" tip={TIPS.status}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o || "any"} value={o}>
                {o ? STATUS_LABELS[o] || o : "Any"}
              </option>
            ))}
          </select>
        </LabelWithTip>
        <LabelWithTip label="Channel" tip={TIPS.channel}>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} style={inputStyle}>
            {CHANNEL_OPTIONS.map((o) => (
              <option key={o || "any"} value={o}>
                {o || "Any"}
              </option>
            ))}
          </select>
        </LabelWithTip>
        <LabelWithTip label="Display name" tip={TIPS.displayName}>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} placeholder="basename" />
        </LabelWithTip>
        <LabelWithTip label="Hash prefix" tip={TIPS.hashPrefix}>
          <input value={hashPrefix} onChange={(e) => setHashPrefix(e.target.value)} style={inputStyle} placeholder="12 hex" />
        </LabelWithTip>
        <LabelWithTip label="Uploader" tip={TIPS.actor}>
          <input value={actorId} onChange={(e) => setActorId(e.target.value)} style={inputStyle} placeholder="username" />
        </LabelWithTip>
        <LabelWithTip label="Error contains" tip={TIPS.errorContains}>
          <input value={errorQ} onChange={(e) => setErrorQ(e.target.value)} style={inputStyle} />
        </LabelWithTip>
        <div
          data-tour="slides-activity-search"
          style={{ display: "flex", alignItems: "end", gap: 6, flexWrap: "wrap" }}
        >
          <button
            type="button"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#2563eb",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
            onClick={() => load({ append: false })}
            disabled={loading}
          >
            {loading ? "Loading…" : "Search"}
          </button>
          <InfoTip text={TIPS.search} />
          <button
            type="button"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#111827",
              fontWeight: 600,
              cursor: "pointer",
            }}
            onClick={resetFilters}
            disabled={loading}
          >
            Reset
          </button>
          <InfoTip text={TIPS.reset} />
        </div>
      </div>

      {error && <p style={{ color: "#b91c1c" }}>{error}</p>}

      <div data-tour="slides-activity-results">
      <table style={adminStyles.table}>
        <thead>
          <tr>
            <ThWithTip tip={TIPS.colExpand} />
            <ThWithTip tip={TIPS.colStarted}>Started</ThWithTip>
            <ThWithTip tip={TIPS.colChannel}>Channel</ThWithTip>
            <ThWithTip tip={TIPS.colName}>Name</ThWithTip>
            <ThWithTip tip={TIPS.colApplication}>Application</ThWithTip>
            <ThWithTip tip={TIPS.colHash}>Hash</ThWithTip>
            <th style={adminStyles.th} data-tour="slides-activity-status">
              <span style={labelRowStyle}>
                Status
                <InfoTip text={TIPS.colStatus} wide />
              </span>
            </th>
            <ThWithTip tip={TIPS.colSlide}>Slide</ThWithTip>
            <ThWithTip tip={TIPS.colActor}>Uploader</ThWithTip>
            <ThWithTip tip={TIPS.colError} align="end">Error</ThWithTip>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading && (
            <tr>
              <td colSpan={10} style={{ padding: 16, color: "#64748b" }}>
                No upload activity found for these filters.
              </td>
            </tr>
          )}
          {items.map((it) => {
            const open = !!expanded[it.session_id];
            const openSlide = canOpenSlideDetail(it);
            const goToSlide = (e) => {
              e.stopPropagation();
              if (!openSlide) return;
              navigate(`/slides/${encodeURIComponent(it.job_id)}`);
            };
            return (
              <Fragment key={it.session_id}>
                <tr
                  style={{ cursor: "pointer" }}
                  aria-expanded={open}
                  title={open ? "Collapse details" : "Expand details"}
                  onClick={() =>
                    setExpanded((prev) => ({
                      ...prev,
                      [it.session_id]: !prev[it.session_id],
                    }))
                  }
                >
                  <td style={{ ...adminStyles.td, width: 28, paddingRight: 0 }}>
                    <span style={expandIconStyle} aria-hidden="true">
                      {open ? "▼" : "▶"}
                    </span>
                  </td>
                  <td style={adminStyles.td}>{formatInTimezone(it.started_at, timezone)}</td>
                  <td style={adminStyles.td}>{it.channel || "—"}</td>
                  <td style={adminStyles.td}>
                    {openSlide ? (
                      <button
                        type="button"
                        style={adminStyles.linkButton}
                        title="Open slide details"
                        onClick={goToSlide}
                      >
                        {it.display_name || "—"}
                      </button>
                    ) : (
                      it.display_name || "—"
                    )}
                  </td>
                  <td style={adminStyles.td}>{it.application || "—"}</td>
                  <td style={{ ...adminStyles.td, fontFamily: "monospace", fontSize: 12 }}>
                    {it.content_hash_prefix || "—"}
                  </td>
                  <td style={adminStyles.td}>
                    <StatusBadge status={visibleStatus(it)} />
                  </td>
                  <td style={adminStyles.td}>
                    {openSlide ? (
                      <button
                        type="button"
                        style={adminStyles.linkButton}
                        title="Open slide details"
                        onClick={goToSlide}
                      >
                        View
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={adminStyles.td}>{it.actor_id || "—"}</td>
                  <td style={{ ...adminStyles.td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.error || "—"}
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={10} style={{ ...adminStyles.td, background: "#f8fafc" }}>
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                        <div>
                          <strong>Session:</strong> {it.session_id}
                        </div>
                        <div>
                          <strong>Application:</strong> {it.application || "—"}
                        </div>
                        <div>
                          <strong>Updated:</strong> {formatInTimezone(it.updated_at, timezone)}
                        </div>
                        <div>
                          <strong>Completed:</strong> {formatInTimezone(it.completed_at, timezone)}
                        </div>
                        <div>
                          <strong>Agent version:</strong> {it.agent_version || "—"}
                        </div>
                        {openSlide && (
                          <div style={{ marginTop: 8 }}>
                            <button
                              type="button"
                              style={adminStyles.linkButton}
                              onClick={goToSlide}
                            >
                              View slide details
                            </button>
                          </div>
                        )}
                        <div style={{ marginTop: 8 }}>
                          <strong>Timeline</strong>
                          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                            {visibleTimeline(it.timeline).map((t, i) => (
                              <li key={i}>
                                {formatInTimezone(t.at, timezone)}: {formatStatusLabel(t.event)}
                                {t.detail ? ` — ${t.detail}` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {nextToken && (
        <button
          type="button"
          style={{
            marginTop: 12,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: "#fff",
            cursor: "pointer",
          }}
          disabled={loading}
          onClick={() => load({ append: true, token: nextToken })}
        >
          Load more
        </button>
      )}
      </div>
    </div>
  );
}
